// frontend/src/pages/Promotions.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Clock, ChevronRight } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

interface Promotion {
  id: number;
  nom: string;
  date_debut: string;
  date_fin: string;
  is_active: boolean;
  product: {
    id: number;
    nom: string;
    description: string;
    heygen_agent_id?: string | null;
  };
  users_count: number;
  completed?: boolean;
}

export default function Promotions() {
  const [availablePromotions, setAvailablePromotions] = useState<Promotion[]>([]);
  const [myPromotions, setMyPromotions] = useState<Promotion[]>([]);
  const [activeTab, setActiveTab] = useState<'available' | 'my'>('my');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const [availableRes, myRes] = await Promise.all([
        api.get('/promotions/available'),
        api.get('/promotions/my-promotions'),
      ]);

      const addStatus = async (promotion: Promotion) => {
        try {
          const modulesRes = await api.get(`/modules/promotion/${promotion.id}`);
          const completed = modulesRes.data.every((m: any) => m.status === 'completed');
          return { ...promotion, completed } as Promotion;
        } catch (err) {
          console.error(`Erreur lors du chargement des modules pour la promotion ${promotion.id}:`, err);
          return { ...promotion, completed: false } as Promotion;
        }
      };

      const availableWithStatus = await Promise.all(
        availableRes.data.map((p: Promotion) => addStatus(p))
      );
      const myWithStatus = await Promise.all(
        myRes.data.map((p: Promotion) => addStatus(p))
      );

      setAvailablePromotions(availableWithStatus);
      setMyPromotions(myWithStatus);
    } catch (error) {
      console.error('Erreur lors du chargement des promotions:', error);
      toast.error('Impossible de charger les promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (promotionId: number) => {
    try {
      await api.post(`/promotions/${promotionId}/register`);
      toast.success('Inscription réussie !');
      fetchPromotions();
    } catch (error) {
      const message = (error as any).response?.data?.detail;
      toast.error(message || "Erreur lors de l'inscription");
    }
  };

  const handleUnregister = async (promotionId: number) => {
    const confirmUnregister = window.confirm(
      'Voulez-vous vraiment vous désinscrire de cette promotion ?'
    );
    if (!confirmUnregister) return;

    try {
      await api.delete(`/promotions/${promotionId}/register`);
      toast.success('Désinscription réussie !');
      fetchPromotions();
    } catch (error) {
      const message = (error as any).response?.data?.detail;
      toast.error(message || 'Erreur lors de la désinscription');
    }
  };

  const handleAccess = (promotion: Promotion) => {
    const shareToken = promotion.product?.heygen_agent_id;
    if (shareToken) {
      const shareUrl = `https://labs.heygen.com/guest/streaming-embed?share=${shareToken}&inIFrame=1`;
      localStorage.setItem('share', shareUrl);
    } else {
      localStorage.removeItem('share');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Mes Promotions</h1>

        {/* Tabs */}
        {/* ⬇️ overflow uniquement sur mobile; visible (donc sans scrollbar) dès sm */}
        <div className="border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto sm:overflow-visible">
          <nav className="-mb-px flex space-x-6 sm:space-x-8 min-w-max">
            <button
              onClick={() => setActiveTab('my')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'my'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mes formations ({myPromotions.length})
            </button>
            <button
              onClick={() => setActiveTab('available')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'available'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Formations disponibles ({availablePromotions.length})
            </button>
          </nav>
        </div>

        {/* Contenu */}
        {activeTab === 'my' ? (
          <div className="space-y-4">
            {myPromotions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Vous n&apos;êtes inscrit à aucune formation actuellement.
              </p>
            ) : (
              myPromotions.map((promotion) => (
                <div
                  key={promotion.id}
                  className="border rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                        {promotion.nom}
                      </h3>
                      <p className="text-gray-600 mt-1 break-words">{promotion.product.nom}</p>
                      {promotion.product.description && (
                        <p className="text-sm text-gray-500 mt-2 break-words">
                          {promotion.product.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 shrink-0" />
                          <span>
                            {formatDate(promotion.date_debut)} - {formatDate(promotion.date_fin)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1 shrink-0" />
                          <span>{promotion.users_count} agents</span>
                        </div>
                        {promotion.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                        {promotion.completed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Terminée
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Non terminée
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full md:w-auto items-center gap-2 md:gap-3">
                      <Link
                        to={`/promotions/${promotion.id}/modules`}
                        onClick={() => handleAccess(promotion)}
                        className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Accéder
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleUnregister(promotion.id)}
                        className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Désinscrire
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {availablePromotions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Aucune formation disponible pour le moment.
              </p>
            ) : (
              availablePromotions.map((promotion) => (
                <div
                  key={promotion.id}
                  className="border rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                        {promotion.nom}
                      </h3>
                      <p className="text-gray-600 mt-1 break-words">{promotion.product.nom}</p>
                      {promotion.product.description && (
                        <p className="text-sm text-gray-500 mt-2 break-words">
                          {promotion.product.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 shrink-0" />
                          <span>
                            {formatDate(promotion.date_debut)} - {formatDate(promotion.date_fin)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1 shrink-0" />
                          <span>{promotion.users_count} agents inscrits</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 shrink-0" />
                          <span>
                            {Math.ceil(
                              (new Date(promotion.date_fin).getTime() - new Date().getTime()) /
                                (1000 * 60 * 60 * 24)
                            )}{' '}
                            jours restants
                          </span>
                        </div>
                        {promotion.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                        {promotion.completed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Terminée
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Non terminée
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleRegister(promotion.id)}
                      className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      S&apos;inscrire
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
